// src/services/api.js
import axios from 'axios'
import i18n from '@/i18n'

const apiHostname = import.meta.env.VITE_API_HOSTNAME

const api = axios.create({
    baseURL: apiHostname,
    // 20-second timeout prevents the loading spinner from hanging indefinitely
    // on a stalled or unresponsive backend connection.
    timeout: 20000
})

// Add a request interceptor
api.interceptors.request.use(
    (config) => {
        // Retrieve the token from localStorage
        const token = localStorage.getItem('authToken')
        // If a token is present, append it to the headers of all requests
        if (token) {
            config.headers['Authorization'] = `Token ${token}`
        }
        // Attach Accept-Language header using the current i18n locale
        const currentLang = i18n.global.locale.value
        config.headers['Accept-Language'] = currentLang
        return config
    },
    (error) => {
        // Properly reject the promise with an Error object
        return Promise.reject(new Error(error.message || 'Failed to process request'))
    }
)
/**
 * Wraps an API function call with try/catch and returns a structured response.
 *
 * @template T
 * @param {() => Promise<import('axios').AxiosResponse<T>>} fn - The async API call function.
 * @returns {Promise<{ success: true, data: T } | { success: false, error: string, status?: number }>}
 */
export const safeApiCall = async (fn) => {
    try {
        const res = await fn()
        return {
            success: true,
            data: res.data
        }
    } catch (err) {
        return {
            success: false,
            error: err?.response?.data?.message || err.message || 'Unknown error',
            status: err?.response?.status || null
        }
    }
}

export default api
